// Libraries
import { useMediaQuery } from 'react-responsive';

// Hooks
import { useAppSelector, useAppDispatch } from '../../../app/hooks';

// Features
import {
    pushMenuStatus,
    pushPlayersListStatus,
    selectMenuStatus,
    selectPlayersListStatus,
} from '../../../features/app/appSlice';
import { clearChatHistory } from '../../../features/chat/chatSlice';
import {
    selectSession,
    selectPlayerList,
    clearSession,
} from '../../../features/session/sessionSlice';
import { selectUser } from '../../../features/user/userSlice';

// Components
import Logo from '../../atoms/Logo/Logo';

// Utilities
import {
    mediaPortrait,
    mediaPortraitMobile,
    mediaLandscapeMobile,
} from '../../../utils/misc/mediaQueries';
import setAvatar from '../../../utils/misc/setAvatar';

// Styles
import styles from './Header.module.css';

/**
 * @function Header
 * Introductory content and navigational aids
 *
 * @example
 * return (
 *   <Header />
 * )
 */
function Header(): JSX.Element {
    const dispatch = useAppDispatch();
    const session = useAppSelector(selectSession);
    const playerList = useAppSelector(selectPlayerList);
    const user = useAppSelector(selectUser);
    const menuOpen = useAppSelector(selectMenuStatus);
    const playersListOpen = useAppSelector(selectPlayersListStatus);
    const isPortrait = useMediaQuery(mediaPortrait);
    const isPortraitMobile = useMediaQuery(mediaPortraitMobile);
    const isLandscapeMobile = useMediaQuery(mediaLandscapeMobile);
    const avatar = setAvatar(user.avatar);

    function handleSettingsClick(event: any) {
        dispatch(pushMenuStatus(!menuOpen));
        dispatch(pushPlayersListStatus(false));
        event.preventDefault();
    }

    function handlePlayersClick(event: any) {
        dispatch(pushPlayersListStatus(!playersListOpen));
        dispatch(pushMenuStatus(false));
        event.preventDefault();
    }

    function handleLeaveClick(event: any) {
        dispatch(clearSession());
        dispatch(clearChatHistory());
        event.preventDefault();
    }

    return (
        <header className={styles.container}>
            <Logo
                type={isPortraitMobile || isLandscapeMobile ? 'circle' : 'full'}
            />
            {session !== null &&
                playerList.length > 0 &&
                !isPortrait &&
                !isLandscapeMobile && (
                    <button
                        type="button"
                        className={`${styles['icon-button']}`}
                        onClick={(e) => handlePlayersClick(e)}
                    >
                        <picture className={styles.icon}>
                            <img
                                src={`https://${process.env.REACT_APP_CDN}/play-bingo/images/icons/icon-players.svg`}
                                alt=""
                            />
                        </picture>
                        Players
                        <br />
                        <b>{playerList.length}</b>
                    </button>
                )}
            {user.id !== 0 && (
                <>
                    <button
                        type="button"
                        className={styles['icon-button']}
                        onClick={(e) => false}
                        disabled
                    >
                        <picture className={styles.icon}>
                            <img
                                src={`https://${process.env.REACT_APP_CDN}/play-bingo/images/icons/icon-access-token-gold.svg`}
                                alt="Access Tokens"
                                className={styles.token}
                            />
                        </picture>
                        <>
                            {' '}
                            <b className={styles.yellow}>
                                {user.active_access_token_count}&nbsp;Access
                                <br />
                                Tokens
                            </b>
                        </>
                    </button>
                    <button
                        type="button"
                        className={styles['icon-button']}
                        onClick={(e) => false}
                        disabled
                    >
                        <picture className={styles.avatar}>
                            <img src={avatar} alt={user.username} />
                        </picture>
                        {isPortrait || isLandscapeMobile ? (
                            <b>
                                {user.points.toLocaleString('en-US')}{' '}
                                Port&nbsp;Pts
                            </b>
                        ) : (
                            <>
                                {user.username}
                                <br />
                                <b>{user.points.toLocaleString('en-US')} PP</b>
                            </>
                        )}
                    </button>
                    <button
                        type="button"
                        className={`${styles.button} ${styles.settings}`}
                        onClick={(e) => handleSettingsClick(e)}
                    >
                        Settings
                    </button>
                </>
            )}
            {session !== null ? (
                <button
                    type="button"
                    className={`${styles.button} ${styles.leave}`}
                    onClick={(e) => handleLeaveClick(e)}
                >
                    Leave
                </button>
            ) : (
                <a
                    href={`https://${process.env.REACT_APP_LOGIN_URL}`}
                    className={`${styles.button} ${styles.leave}`}
                >
                    Exit
                </a>
            )}
        </header>
    );
}

// Exports
export default Header;
